import { computed, reactive, readonly, ref } from 'vue'
import {
  cancelCallbackVerification as cancelCallbackVerificationApi,
  ensureTelephony,
  getCallbackNumbers,
  getTelephony,
  recallCallbackVerification as recallCallbackVerificationApi,
  startCallbackVerification as startCallbackVerificationApi,
  type CallbackTarget,
  type TelephonyProjection,
} from '../../services/dialerApi'
import { useCurrentUser } from '../../services/user'
import { useContextDrawer } from '../../drawer/useContextDrawer'
import { DIALER_SETTINGS_CONTEXT_TYPE } from './dialerSettingsContext'
import { inReview, mostUrgent, sectionDots as computeSectionDots, type DotKind } from '../components/dialer/settings/phoneSettingsNav'

// Drawer surfaces. Only two: the drawer is personal scope now, and its one
// drill-in is the callback-number verify flow. Team surfaces (business, voice
// integrity, team numbers, team callback, guardrails, CRM export) live on
// /settings/phone, which guards itself against non-admins.
export type TelephonySurface = 'home' | 'callback_own'

export type VerifyPhase = 'idle' | 'calling' | 'waiting' | 'failed' | 'verified'

export interface PillDisplay {
  label: string
  tone: 'amber' | 'success'
  action: 'prefs' | 'phone_settings' | null
  hash?: string // Section anchor on /settings/phone for the phone_settings action.
}

// ---------------------------------------------------------------------------
// Module-level singleton state
// ---------------------------------------------------------------------------

const { isAdmin } = useCurrentUser()

// The Preferences panel now lives in the shared app context drawer
// (useContextDrawer) rather than a bespoke overlay: "open" == a dialer_settings
// context is active AND the drawer is expanded. useTelephony still owns the
// surface state + verify machine; the context drawer owns the shell.
const _ctx = useContextDrawer()

const projection = ref<TelephonyProjection | null>(null)
const activeSurface = ref<TelephonySurface>('home')

const settingsOpen = computed(
  () => _ctx.drawerContext.value?.type === DIALER_SETTINGS_CONTEXT_TYPE && _ctx.drawerOpen.value,
)

// Set by PhoneSettingsPage while it is mounted. The business / voice-integrity
// facets moved there, so the slow refresh below has to follow them: gated on the
// drawer alone, an admin watching "In review" on the page would never see it
// flip without a manual reload.
const pageOpen = ref(false)
function setPageOpen(open: boolean): void {
  pageOpen.value = open
  if (open) _maybeSlowRefresh()
  else _clearSlowRefresh()
}
const watchingFacets = computed(() => settingsOpen.value || pageOpen.value)

// Call-and-code verification state machine (shared: the onboarding card + the
// drawer's own/team callback surfaces — only one runs at a time).
const verify = reactive({
  phase: 'idle' as VerifyPhase,
  target: 'own' as CallbackTarget,
  phoneNumber: '',
  label: '',
  code: '',
  secondsLeft: 0,
  // Only the timeout (countdown hit 0) vs a generic failure are distinguished —
  // the wire carries no per-reason detail.
  failReason: '' as '' | 'generic' | 'timeout',
})

// Timer tokens (setTimeout-chain pattern from useDialerSession.verifyBridge):
// bumping the token invalidates all in-flight timers keyed to the old value.
let verifyToken = 0
let slowToken = 0
let ensurePromise: Promise<void> | null = null
let writeSeq = 0

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function _facetPending(): boolean {
  const p = projection.value
  if (!p) return false
  return inReview(p.business_profile.state) || inReview(p.voice_integrity.state)
}

function _clearSlowRefresh(): void {
  slowToken++
}

// Reschedules itself while the drawer OR the settings page is open and a facet
// is under review.
function _maybeSlowRefresh(): void {
  _clearSlowRefresh()
  if (!watchingFacets.value || !_facetPending()) return
  const token = slowToken
  setTimeout(() => {
    if (token !== slowToken) return
    // refresh() re-invokes _maybeSlowRefresh() → next tick reschedules
    void refresh()
  }, 30000)
}

function _stopVerifyTimers(): void {
  verifyToken++
}

function _secondsUntil(expiresAt: string): number {
  const t = new Date(expiresAt).getTime()
  if (Number.isNaN(t)) return 60
  return Math.max(0, Math.round((t - Date.now()) / 1000))
}

function _runCountdown(token: number): void {
  const tick = () => {
    if (token !== verifyToken || verify.phase !== 'waiting') return
    if (verify.secondsLeft <= 0) {
      verify.phase = 'failed'
      verify.failReason = 'timeout'
      _stopVerifyTimers()
      return
    }
    setTimeout(() => {
      if (token !== verifyToken || verify.phase !== 'waiting') return
      verify.secondsLeft = Math.max(0, verify.secondsLeft - 1)
      tick()
    }, 1000)
  }
  tick()
}

function _runStatusPoll(token: number): void {
  setTimeout(async () => {
    if (token !== verifyToken || verify.phase !== 'waiting') return
    let state
    try {
      state = await getCallbackNumbers()
    } catch {
      _runStatusPoll(token) // transient read error — retry next tick
      return
    }
    if (token !== verifyToken || verify.phase !== 'waiting') return
    let status: 'verified' | 'pending' | 'failed'
    if (verify.target === 'team') {
      const td = state.team_default
      if (td.pending_number) status = 'pending'
      else if (td.verified && td.phone_number === verify.phoneNumber) status = 'verified'
      else status = 'failed'
    } else {
      status = state.me?.status ?? 'pending'
    }
    if (status === 'verified') {
      verify.phase = 'verified'
      _stopVerifyTimers()
      void refresh()
      return
    }
    if (status === 'failed') {
      verify.phase = 'failed'
      verify.failReason = 'generic'
      _stopVerifyTimers()
      return
    }
    _runStatusPoll(token)
  }, 2000)
}

function _startVerifyTimers(): void {
  const token = ++verifyToken
  _runCountdown(token)
  _runStatusPoll(token)
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

// Idempotent provisioning trigger. In-flight-deduped; never throws to the page
// (a failed ensure just leaves the projection null/stale).
async function ensure(): Promise<void> {
  if (ensurePromise) return ensurePromise
  ensurePromise = (async () => {
    const seq = writeSeq
    try {
      const p = await ensureTelephony()
      if (seq === writeSeq) projection.value = p
    } catch (e) {
      console.warn('[useTelephony] ensure failed', e)
    } finally {
      ensurePromise = null
    }
  })()
  return ensurePromise
}

async function refresh(): Promise<void> {
  const seq = writeSeq
  try {
    const p = await getTelephony()
    if (seq === writeSeq) projection.value = p
  } catch (e) {
    console.warn('[useTelephony] refresh failed', e)
  }
  _maybeSlowRefresh()
}

// For writes that already return the projection (e.g. the guardrails PUT).
function applyProjection(p: TelephonyProjection): void {
  writeSeq++
  projection.value = p
  _maybeSlowRefresh()
}

function setSurface(surface: TelephonySurface): void {
  activeSurface.value = surface
}

function openDrawer(surface?: TelephonySurface): void {
  // Don't resurface a completed verification from a prior flow.
  if (verify.phase === 'verified') resetVerify()
  setSurface(surface || 'home')
  // Take over the shared context drawer slot. On the dialer page this replaces
  // the contact context; dismissing the panel collapses back to the parked
  // settings rail (the page's default), not to a contact.
  _ctx.setContext({ type: DIALER_SETTINGS_CONTEXT_TYPE, surface: activeSurface.value })
  _ctx.openDrawer()
  void refresh()
}

// Collapse the panel (× / \ / Esc). On the dialer page the page's reclaim watcher
// turns any collapse into "return to the contact"; teardown bookkeeping happens in
// onPanelClosed when the panel actually unmounts.
function closeDrawer(): void {
  _ctx.closeDrawer({ byUser: true })
}

// Called from DialerSettingsContextPanel.onUnmounted — resets surface + stops the
// slow refresh. Must NOT touch the context (the page has already re-set the contact
// context by the time this fires).
function onPanelClosed(): void {
  activeSurface.value = 'home'
  _clearSlowRefresh()
}

async function startCallbackVerification(opts: {
  phone_number: string
  label?: string
  target: CallbackTarget
}): Promise<void> {
  _stopVerifyTimers()
  const myToken = verifyToken
  verify.phase = 'calling'
  verify.target = opts.target
  verify.phoneNumber = opts.phone_number
  verify.label = opts.label || ''
  verify.code = ''
  verify.failReason = ''
  verify.secondsLeft = 0
  try {
    const res = await startCallbackVerificationApi({
      phone_number: opts.phone_number,
      label: opts.label,
      target: opts.target,
    })
    if (myToken !== verifyToken) return // cancelled / superseded while calling
    // Adopt the server's normalized number — the raw input can differ from what was
    // stamped, and _runStatusPoll compares this against the stored number.
    verify.phoneNumber = res.phone_number || opts.phone_number
    if (res.already_verified) {
      // Already a verified caller ID.
      verify.phase = 'verified'
      void refresh()
      return
    }
    verify.code = res.validation_code
    verify.secondsLeft = _secondsUntil(res.expires_at)
    verify.phase = 'waiting'
    _startVerifyTimers()
  } catch {
    if (myToken !== verifyToken) return
    verify.phase = 'failed'
    verify.failReason = 'generic'
  }
}

async function recall(target: CallbackTarget): Promise<void> {
  _stopVerifyTimers()
  const myToken = verifyToken
  verify.phase = 'calling'
  verify.target = target
  verify.code = ''
  verify.failReason = ''
  verify.secondsLeft = 0
  try {
    const res = await recallCallbackVerificationApi(target)
    if (myToken !== verifyToken) return
    // Recall re-reads the number server-side, so this is the only place it's set —
    // the caller passes a target, not a number.
    verify.phoneNumber = res.phone_number || verify.phoneNumber
    if (res.already_verified) {
      // Same already-verified short-circuit as startCallbackVerification.
      verify.phase = 'verified'
      void refresh()
      return
    }
    verify.code = res.validation_code
    verify.secondsLeft = _secondsUntil(res.expires_at)
    verify.phase = 'waiting'
    _startVerifyTimers()
  } catch {
    if (myToken !== verifyToken) return
    verify.phase = 'failed'
    verify.failReason = 'generic'
  }
}

async function cancelVerification(target: CallbackTarget): Promise<void> {
  _stopVerifyTimers()
  verify.phase = 'idle'
  verify.code = ''
  verify.secondsLeft = 0
  verify.failReason = ''
  try {
    await cancelCallbackVerificationApi(target)
  } catch {
    /* best-effort — the client-side stop is the source of truth */
  }
  void refresh()
}

function resetVerify(): void {
  _stopVerifyTimers()
  verify.phase = 'idle'
  verify.code = ''
  verify.secondsLeft = 0
  verify.failReason = ''
}

function stopAllPolling(): void {
  // A live call-and-code verification is a short flow the rep started on
  // purpose, and its timers self-terminate the moment the phase leaves
  // 'waiting'. Killing them on unmount froze the panel mid-countdown.
  if (verify.phase !== 'calling' && verify.phase !== 'waiting') _stopVerifyTimers()
  _clearSlowRefresh()
}

// ---------------------------------------------------------------------------
// Derived signals (chrome)
// ---------------------------------------------------------------------------

const sectionDots = computed(() => (projection.value ? computeSectionDots(projection.value) : null))
const orgDot = computed<DotKind>(() =>
  sectionDots.value ? mostUrgent(Object.values(sectionDots.value)) : 'none',
)
const orgIncomplete = computed(() => !projection.value || orgDot.value === 'todo')

const ownCallbackUnverified = computed(() => {
  const cb = projection.value?.me.callback_number
  return !cb || cb.status !== 'verified'
})

const myPrefsDot = computed<DotKind>(() =>
  projection.value && ownCallbackUnverified.value ? 'todo' : 'none',
)
// Team — the /settings/phone section dots rolled up.
const orgSetupDot = computed<DotKind>(() => (isAdmin.value ? orgDot.value : 'none'))

// "Setup incomplete" is personal: only the viewer's own unverified callback, which
// Preferences fixes. Unfinished team setup shows as the Phone settings dot.
const pill = computed<PillDisplay | null>(() => {
  const p = projection.value
  if (!p) return null

  if (ownCallbackUnverified.value) return { label: 'Setup incomplete', tone: 'amber', action: 'prefs' }
  if (!isAdmin.value) return null

  if (p.pill === 'verification_in_progress') {
    return { label: 'Verification in progress', tone: 'amber', action: 'phone_settings', hash: '#reputation' }
  }
  if (p.pill === 'verified' && !orgIncomplete.value) {
    return { label: '✓ Business verified', tone: 'success', action: null }
  }
  return null
})

// ---------------------------------------------------------------------------
// Composable
// ---------------------------------------------------------------------------

export function useTelephony() {
  return {
    // Raw ref (not readonly): nested facets are passed straight into typed
    // component props; components treat it as read-only by convention.
    projection,
    settingsOpen,
    activeSurface: readonly(activeSurface),
    verify: readonly(verify),
    isAdmin,

    // derived
    pill,
    sectionDots,
    myPrefsDot,
    orgSetupDot,

    // actions
    ensure,
    refresh,
    applyProjection,
    setPageOpen,
    openDrawer,
    closeDrawer,
    onPanelClosed,
    setSurface,
    startCallbackVerification,
    recall,
    cancelVerification,
    resetVerify,
    stopAllPolling,
  }
}
